import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Disclosure, Transition } from '@headlessui/react';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';
import { type Plugin as BasePlugin } from '../../context/PluginContext';
import { APP_CONFIG } from '../../config/constants';

// --- Ícones ---
const ChevronUpIcon = ({ className }: { className?: string }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>;
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>;

// --- Tipos de Dados ---
interface PluginVersion { id: number; version: string; changelog: string; fileUrl: string; createdAt: string; pluginId: string; }
interface Category { id: number; name: string; slug: string; icon: string; }
interface EditablePlugin extends Omit<BasePlugin, 'compatibleVersions' | 'mainServerSoftware' | 'dependencies'> {
    versions: PluginVersion[];
    categoryIds: number[];
    compatibleVersions: string;
    mainServerSoftware: string;
    dependencies: string;
    overview?: string;
    commands?: string;
    permissions?: string;
    demoVideoUrl?: string;
}

const emptyPlugin: EditablePlugin = {
    id: '', slug: '', name: '', authorId: '', tagline: '', description: '', price: 0, iconUrl: '', bannerUrl: '', rating: 0, reviewCount: 0, purchaseCount: 0, downloadCount: 0, status: 'draft', isFeatured: false, currentVersion: '', overview: '', commands: '', permissions: '', demoVideoUrl: '', versions: [], categoryIds: [], compatibleVersions: '[]', mainServerSoftware: '[]', dependencies: '[]',
};

// --- Sub-componente: Adicionar Versão ---
function AddVersionForm({ pluginId, onVersionAdded }: { pluginId: string; onVersionAdded: () => void; }) {
    const [version, setVersion] = useState('');
    const [changelog, setChangelog] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [status, setStatus] = useState({ loading: false, error: '' });
    const { showNotification } = useNotificationDispatch();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // Validar tipo de arquivo
        const allowedExtensions = ['.jar', '.zip'];
        const fileName = selectedFile.name.toLowerCase();
        const isValid = allowedExtensions.some(ext => fileName.endsWith(ext));

        if (!isValid) {
            setStatus({ loading: false, error: 'Apenas arquivos .jar e .zip são permitidos' });
            setFile(null);
            return;
        }

        // Validar tamanho (25MB)
        const maxSize = 25 * 1024 * 1024;
        if (selectedFile.size > maxSize) {
            setStatus({ loading: false, error: 'O arquivo deve ter no máximo 25MB' });
            setFile(null);
            return;
        }

        setFile(selectedFile);
        setStatus({ loading: false, error: '' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!file) {
            setStatus({ loading: false, error: 'Selecione um arquivo .jar ou .zip' });
            return;
        }

        setStatus({ loading: true, error: '' });
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('version', version);
            formData.append('changelog', changelog);

            await apiClient.post(`/admin/plugins/${pluginId}/versions`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setVersion('');
            setChangelog('');
            setFile(null);
            setStatus({ loading: false, error: '' });

            // Limpar o input de arquivo
            const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
            if (fileInput) fileInput.value = '';

            showNotification('Nova versão adicionada com sucesso!', 'success');
            onVersionAdded();
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || 'Falha ao adicionar a versão.';
            setStatus({ loading: false, error: errorMsg });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-4 bg-gray-50 rounded-b-lg border-t space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Versão *
                </label>
                <input
                    type="text"
                    value={version}
                    onChange={e => setVersion(e.target.value)}
                    placeholder="Ex: 1.2.4"
                    required
                    className="form-input w-full"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Changelog
                </label>
                <textarea
                    value={changelog}
                    onChange={e => setChangelog(e.target.value)}
                    placeholder="Notas da versão (o que há de novo, correções, etc)..."
                    rows={4}
                    className="form-input w-full"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Arquivo do Plugin * (.jar ou .zip - máx 25MB)
                </label>
                <div className="mt-1 flex items-center gap-3">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand-blue rounded-lg hover:bg-blue-700 transition-all">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Escolher Arquivo
                        <input
                            type="file"
                            accept=".jar,.zip"
                            onChange={handleFileChange}
                            className="hidden"
                            disabled={status.loading}
                        />
                    </label>
                    {file && (
                        <div className="flex items-center gap-2 text-sm">
                            <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-gray-700 font-medium">{file.name}</span>
                            <span className="text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                    )}
                </div>
            </div>

            {status.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-red-700">{status.error}</p>
                </div>
            )}

            <button
                type="submit"
                disabled={status.loading || !file}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-brand-blue to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
                {status.loading ? (
                    <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enviando...
                    </>
                ) : (
                    <>
                        <PlusIcon />
                        Adicionar Versão
                    </>
                )}
            </button>
        </form>
    );
}

// --- Sub-componente: Gerenciar Versão ---
function VersionManager({ version: initialVersion, onUpdate, onDelete }: { version: PluginVersion; onUpdate: () => void; onDelete: (id: number) => void; }) {
    const [isEditing, setIsEditing] = useState(false);
    const [version, setVersion] = useState(initialVersion.version);
    const [changelog, setChangelog] = useState(initialVersion.changelog);
    const [file, setFile] = useState<File | null>(null);
    const [status, setStatus] = useState({ loading: false, error: '' });
    const { showNotification } = useNotificationDispatch();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        const allowedExtensions = ['.jar', '.zip'];
        const fileName = selectedFile.name.toLowerCase();
        const isValid = allowedExtensions.some(ext => fileName.endsWith(ext));

        if (!isValid) {
            setStatus({ loading: false, error: 'Apenas arquivos .jar e .zip são permitidos' });
            setFile(null);
            return;
        }

        const maxSize = 25 * 1024 * 1024;
        if (selectedFile.size > maxSize) {
            setStatus({ loading: false, error: 'O arquivo deve ter no máximo 25MB' });
            setFile(null);
            return;
        }

        setFile(selectedFile);
        setStatus({ loading: false, error: '' });
    };

    const handleUpdate = async () => {
        setStatus({ loading: true, error: '' });
        try {
            const formData = new FormData();
            formData.append('version', version);
            formData.append('changelog', changelog);

            if (file) {
                formData.append('file', file);
            }

            await apiClient.put(`/admin/plugins/${initialVersion.pluginId}/versions/${initialVersion.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setIsEditing(false);
            setFile(null);
            setStatus({ loading: false, error: '' });
            showNotification('Versão atualizada com sucesso!', 'success');
            onUpdate();
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || 'Erro ao atualizar versão.';
            setStatus({ loading: false, error: errorMsg });
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setVersion(initialVersion.version);
        setChangelog(initialVersion.changelog);
        setFile(null);
        setStatus({ loading: false, error: '' });
    };

    return (
        <div className="p-4 border rounded-md bg-white">
            {isEditing ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Versão *</label>
                        <input
                            type="text"
                            value={version}
                            onChange={e => setVersion(e.target.value)}
                            className="form-input w-full"
                            placeholder="Ex: 1.0.0"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Changelog</label>
                        <textarea
                            value={changelog}
                            onChange={e => setChangelog(e.target.value)}
                            rows={3}
                            className="form-input w-full"
                            placeholder="Notas da versão..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Substituir Arquivo (opcional)
                        </label>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                            <p className="text-xs text-blue-700">
                                <strong>Arquivo atual:</strong> {initialVersion.fileUrl.split('/').pop()}
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                                Deixe em branco para manter o arquivo atual, ou selecione um novo para substituir.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-brand-blue bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                Escolher Novo Arquivo
                                <input
                                    type="file"
                                    accept=".jar,.zip"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    disabled={status.loading}
                                />
                            </label>
                            {file && (
                                <div className="flex items-center gap-2 text-sm">
                                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-gray-700 font-medium">{file.name}</span>
                                    <span className="text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {status.error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm text-red-700">{status.error}</p>
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button
                            onClick={handleUpdate}
                            disabled={status.loading}
                            className="px-4 py-2 text-sm font-semibold text-white bg-brand-blue rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-all flex items-center gap-2"
                        >
                            {status.loading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Salvar Alterações
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleCancel}
                            className="px-4 py-2 text-sm font-semibold text-text-secondary bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
                            disabled={status.loading}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex justify-between items-start">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <p className="font-bold text-lg">Versão {version}</p>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {initialVersion.fileUrl.endsWith('.jar') ? '.jar' : '.zip'}
                            </span>
                        </div>
                        <p className="text-xs text-text-secondary mt-1">Lançada em: {new Date(initialVersion.createdAt).toLocaleDateString('pt-BR')}</p>
                        {changelog && (
                            <>
                                <p className="font-semibold text-sm mt-3 mb-1">Changelog:</p>
                                <pre className="text-sm text-text-secondary whitespace-pre-wrap font-sans">{changelog}</pre>
                            </>
                        )}
                        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <span>Arquivo armazenado no servidor</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <button onClick={() => setIsEditing(true)} className="text-indigo-600 text-sm font-semibold hover:text-indigo-700">Editar</button>
                        <button onClick={() => onDelete(initialVersion.id)} className="text-red-600 text-sm font-semibold hover:text-red-700">Excluir</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Sub-componente: Upload de Imagens (Ícone e Banner) ---
function ImageUploadSection({
    pluginId,
    currentIconUrl,
    currentBannerUrl,
    onImageUpdated
}: {
    pluginId: string;
    currentIconUrl?: string;
    currentBannerUrl?: string;
    onImageUpdated: () => void;
}) {
    const [iconFile, setIconFile] = useState<File | null>(null);
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState<string | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);
    const [uploadingIcon, setUploadingIcon] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [error, setError] = useState({ icon: '', banner: '' });
    const { showNotification } = useNotificationDispatch();

    const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError(prev => ({ ...prev, icon: 'Apenas imagens JPG, PNG ou WEBP são permitidas' }));
            return;
        }

        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            setError(prev => ({ ...prev, icon: 'A imagem deve ter no máximo 10MB' }));
            return;
        }

        const img = new Image();
        img.onload = () => {
            if (img.width < 500 || img.height < 500) {
                setError(prev => ({ ...prev, icon: 'O ícone deve ter no mínimo 500x500 pixels' }));
                setIconFile(null);
                setIconPreview(null);
                return;
            }

            setIconFile(file);
            setIconPreview(URL.createObjectURL(file));
            setError(prev => ({ ...prev, icon: '' }));
        };
        img.src = URL.createObjectURL(file);
    };

    const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError(prev => ({ ...prev, banner: 'Apenas imagens JPG, PNG ou WEBP são permitidas' }));
            return;
        }

        const maxSize = 15 * 1024 * 1024; // 15MB
        if (file.size > maxSize) {
            setError(prev => ({ ...prev, banner: 'A imagem deve ter no máximo 15MB' }));
            return;
        }

        const img = new Image();
        img.onload = () => {
            if (img.width < 700 || img.height < 700) {
                setError(prev => ({ ...prev, banner: 'O banner deve ter no mínimo 700x700 pixels' }));
                setBannerFile(null);
                setBannerPreview(null);
                return;
            }

            setBannerFile(file);
            setBannerPreview(URL.createObjectURL(file));
            setError(prev => ({ ...prev, banner: '' }));
        };
        img.src = URL.createObjectURL(file);
    };

    const handleUploadIcon = async () => {
        if (!iconFile) return;

        setUploadingIcon(true);
        setError(prev => ({ ...prev, icon: '' }));

        try {
            const formData = new FormData();
            formData.append('icon', iconFile);

            await apiClient.post(`/admin/plugins/${pluginId}/upload-icon`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            showNotification('Ícone enviado com sucesso!', 'success');
            setIconFile(null);
            setIconPreview(null);
            onImageUpdated();
        } catch (err: any) {
            setError(prev => ({ ...prev, icon: err.response?.data?.error || 'Erro ao fazer upload do ícone' }));
        } finally {
            setUploadingIcon(false);
        }
    };

    const handleUploadBanner = async () => {
        if (!bannerFile) return;

        setUploadingBanner(true);
        setError(prev => ({ ...prev, banner: '' }));

        try {
            const formData = new FormData();
            formData.append('banner', bannerFile);

            await apiClient.post(`/admin/plugins/${pluginId}/upload-banner`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            showNotification('Banner enviado com sucesso!', 'success');
            setBannerFile(null);
            setBannerPreview(null);
            onImageUpdated();
        } catch (err: any) {
            setError(prev => ({ ...prev, banner: err.response?.data?.error || 'Erro ao fazer upload do banner' }));
        } finally {
            setUploadingBanner(false);
        }
    };

    const handleRemoveIcon = async () => {
        if (!currentIconUrl) return;

        setUploadingIcon(true);
        try {
            await apiClient.delete(`/admin/plugins/${pluginId}/icon`);
            showNotification('Ícone removido com sucesso!', 'success');
            onImageUpdated();
        } catch (err: any) {
            setError(prev => ({ ...prev, icon: err.response?.data?.error || 'Erro ao remover ícone' }));
        } finally {
            setUploadingIcon(false);
        }
    };

    const handleRemoveBanner = async () => {
        if (!currentBannerUrl) return;

        setUploadingBanner(true);
        try {
            await apiClient.delete(`/admin/plugins/${pluginId}/banner`);
            showNotification('Banner removido com sucesso!', 'success');
            onImageUpdated();
        } catch (err: any) {
            setError(prev => ({ ...prev, banner: err.response?.data?.error || 'Erro ao remover banner' }));
        } finally {
            setUploadingBanner(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Ícone do Plugin */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-200">
                <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="text-xl">🎨</span>
                    Ícone do Plugin
                </h4>
                <div className="flex flex-row items-center gap-5">
                    <div className="relative flex-shrink-0">
                        <div className="w-24 h-24 rounded-xl overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 border-2 border-white shadow-lg">
                            {iconPreview || currentIconUrl ? (
                                <img
                                    src={iconPreview || `${APP_CONFIG.uploadsBaseUrl}${currentIconUrl}`}
                                    alt="Ícone"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
                                    <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-800 mb-0.5">Escolha uma imagem</p>
                        <p className="text-xs text-gray-600 mb-3">JPG, PNG ou WEBP • Máx 10MB • Mín 500x500px</p>
                        <div className="flex flex-wrap gap-2">
                            <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all shadow-md hover:shadow-lg">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Escolher Arquivo
                                <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    onChange={handleIconChange}
                                    className="hidden"
                                    disabled={uploadingIcon}
                                />
                            </label>
                            {iconFile && (
                                <button
                                    type="button"
                                    onClick={handleUploadIcon}
                                    disabled={uploadingIcon}
                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 rounded-lg hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-400 transition-all shadow-md"
                                >
                                    {uploadingIcon ? 'Enviando...' : 'Enviar'}
                                </button>
                            )}
                            {currentIconUrl && !iconFile && (
                                <button
                                    type="button"
                                    onClick={handleRemoveIcon}
                                    disabled={uploadingIcon}
                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-all"
                                >
                                    Remover
                                </button>
                            )}
                        </div>
                        {iconFile && (
                            <div className="flex items-center gap-2 text-xs text-gray-600 mt-2">
                                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-medium">{iconFile.name}</span>
                                <span className="text-gray-500">({(iconFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                        )}
                        {error.icon && <p className="text-red-600 text-sm mt-2">{error.icon}</p>}
                    </div>
                </div>
            </div>

            {/* Banner do Plugin */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
                <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="text-xl">🖼️</span>
                    Banner do Plugin
                </h4>
                <div className="space-y-4">
                    <div className="relative w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 border-4 border-white shadow-xl">
                        {bannerPreview || currentBannerUrl ? (
                            <img
                                src={bannerPreview || `${APP_CONFIG.uploadsBaseUrl}${currentBannerUrl}`}
                                alt="Banner"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500">
                                <svg className="w-20 h-20 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        )}
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-800 mb-1">Escolha uma imagem</p>
                        <p className="text-xs text-gray-600 mb-4">JPG, PNG ou WEBP • Máx 15MB • Mín 700x700px</p>
                        <div className="flex flex-wrap gap-2">
                            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all shadow-md hover:shadow-lg">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Escolher Arquivo
                                <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    onChange={handleBannerChange}
                                    className="hidden"
                                    disabled={uploadingBanner}
                                />
                            </label>
                            {bannerFile && (
                                <button
                                    type="button"
                                    onClick={handleUploadBanner}
                                    disabled={uploadingBanner}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 rounded-xl hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-400 transition-all shadow-md"
                                >
                                    {uploadingBanner ? 'Enviando...' : 'Enviar'}
                                </button>
                            )}
                            {currentBannerUrl && !bannerFile && (
                                <button
                                    type="button"
                                    onClick={handleRemoveBanner}
                                    disabled={uploadingBanner}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100 disabled:opacity-50 transition-all"
                                >
                                    Remover
                                </button>
                            )}
                        </div>
                        {error.banner && <p className="text-red-600 text-sm mt-2">{error.banner}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- Componente Principal da Página ---
export default function AdminPluginEditPage() {
    const { pluginId } = useParams<{ pluginId: string }>();
    const { showNotification } = useNotificationDispatch();
    const [plugin, setPlugin] = useState<EditablePlugin>(emptyPlugin);
    const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const fetchPluginData = useCallback(async () => {
        if (!pluginId) return;
        setLoading(true);
        try {
            const [pluginResponse, categoriesResponse] = await Promise.all([
                apiClient.get(`/admin/plugins/${pluginId}`),
                apiClient.get('/admin/categories')
            ]);

            const pluginData = pluginResponse.data;
            setAvailableCategories(categoriesResponse.data);
            
            setPlugin({
                ...pluginData,
                compatibleVersions: JSON.stringify(pluginData.compatibleVersions || [], null, 2),
                mainServerSoftware: JSON.stringify(pluginData.mainServerSoftware || [], null, 2),
                dependencies: JSON.stringify(pluginData.dependencies || [], null, 2),
            });
            setSelectedCategoryIds(pluginData.categoryIds || []);

        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao carregar dados do plugin.', 'error');
        } finally {
            setLoading(false);
        }
    }, [pluginId, showNotification]);

    useEffect(() => { fetchPluginData(); }, [fetchPluginData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const isCheckbox = type === 'checkbox';
        setPlugin(prev => ({ ...prev, [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value }));
    };

    const handleCategoryChange = (categoryId: number, isChecked: boolean) => {
        setSelectedCategoryIds(prevIds => isChecked ? [...prevIds, categoryId] : prevIds.filter(id => id !== categoryId));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        let parsedJson = {};
        try {
            parsedJson = {
                compatibleVersions: JSON.parse(plugin.compatibleVersions || '[]'),
                mainServerSoftware: JSON.parse(plugin.mainServerSoftware || '[]'),
                dependencies: JSON.parse(plugin.dependencies || '[]'),
            };
        } catch (jsonError: any) {
            showNotification(`JSON inválido: ${jsonError.message}. Verifique os campos de compatibilidade.`, 'error');
            setSaving(false);
            return;
        }

        const payload = { ...plugin, ...parsedJson, categoryIds: selectedCategoryIds };

        try {
            await apiClient.put(`/admin/plugins/${pluginId}`, payload);
            showNotification('Plugin atualizado com sucesso!', 'success');
            window.scrollTo(0, 0);
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Falha ao salvar o plugin.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteVersion = async (versionId: number) => {
        if (!window.confirm("Tem certeza que deseja deletar esta versão? Esta ação não pode ser desfeita.")) return;
        try {
            await apiClient.delete(`/admin/plugins/${pluginId}/versions/${versionId}`);
            showNotification('Versão deletada com sucesso!', 'success');
            fetchPluginData(); // Recarrega todos os dados
        } catch (err: any) {
            showNotification(err.response?.data?.error || 'Erro ao deletar a versão.', 'error');
        }
    };

    if (loading) return <p>Carregando...</p>;

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Editar Plugin: {plugin.name}</h1>
                <Link to="/admin/plugins" className="px-4 py-2 text-sm font-semibold text-text-secondary bg-white border border-border-color rounded-lg hover:bg-gray-50">
                    &larr; Voltar
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Seção de Informações Gerais */}
                <div className="bg-white p-6 rounded-lg shadow-subtle">
                    <h2 className="text-xl font-bold text-text-primary border-b pb-3 mb-4">Informações Gerais</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div><label className="form-label">Nome do Plugin</label><input type="text" name="name" value={plugin.name} onChange={handleChange} required className="form-input" /></div>
                        <div><label className="form-label">Slug (URL)</label><input type="text" name="slug" value={plugin.slug} onChange={handleChange} required className="form-input" /></div>
                    </div>
                    <div className="mt-4"><label className="form-label">Tagline (Slogan)</label><input type="text" name="tagline" value={plugin.tagline} onChange={handleChange} className="form-input" /></div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                        <div><label className="form-label">Preço (R$)</label><input type="number" name="price" value={plugin.price} onChange={handleChange} step="0.01" className="form-input" /></div>
                        <div><label className="form-label">Status</label><select name="status" value={plugin.status} onChange={handleChange} className="form-input"><option value="published">Publicado</option><option value="draft">Rascunho</option><option value="archived">Arquivado</option></select></div>
                        <div className="flex items-end pb-2"><label className="flex items-center gap-2"><input type="checkbox" name="isFeatured" checked={plugin.isFeatured} onChange={handleChange} className="form-checkbox"/> Marcar como Destaque</label></div>
                    </div>
                </div>

                {/* Seção de Conteúdo */}
                <div className="bg-white p-6 rounded-lg shadow-subtle">
                    <h2 className="text-xl font-bold text-text-primary border-b pb-3 mb-4">Conteúdo do Plugin</h2>
                    <div className="space-y-4">
                        <div><label className="form-label">Descrição Curta</label><textarea name="description" value={plugin.description} onChange={handleChange} rows={3} className="form-input" /></div>
                        <div><label className="form-label">Visão Geral (Overview)</label><textarea name="overview" value={plugin.overview || ''} onChange={handleChange} rows={6} className="form-input" /></div>
                        <div><label className="form-label">Comandos</label><textarea name="commands" value={plugin.commands || ''} onChange={handleChange} rows={4} className="form-input" /></div>
                        <div><label className="form-label">Permissões</label><textarea name="permissions" value={plugin.permissions || ''} onChange={handleChange} rows={4} className="form-input" /></div>
                    </div>
                </div>

                {/* Seção de Mídia */}
                <div className="bg-white p-6 rounded-lg shadow-subtle">
                    <h2 className="text-xl font-bold text-text-primary border-b pb-3 mb-4">Mídia</h2>

                    {/* Upload de Ícone e Banner */}
                    <ImageUploadSection
                        pluginId={pluginId!}
                        currentIconUrl={plugin.iconUrl}
                        currentBannerUrl={plugin.bannerUrl}
                        onImageUpdated={fetchPluginData}
                    />

                    {/* URL do Vídeo */}
                    <div className="mt-6">
                        <label className="form-label">URL do Vídeo de Demonstração (YouTube)</label>
                        <input
                            type="text"
                            name="demoVideoUrl"
                            value={plugin.demoVideoUrl || ''}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="https://youtube.com/watch?v=..."
                        />
                    </div>
                </div>

                {/* Seção de Compatibilidade */}
                <div className="bg-white p-6 rounded-lg shadow-subtle">
                    <h2 className="text-xl font-bold text-text-primary border-b pb-3 mb-4">Metadados & Compatibilidade</h2>
                    <div><label className="form-label">Versão Atual do Plugin</label><input type="text" name="currentVersion" value={plugin.currentVersion} onChange={handleChange} className="form-input" placeholder="Ex: 1.10.5" /></div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                        <div><label className="form-label">Versões Compatíveis</label><textarea name="compatibleVersions" value={plugin.compatibleVersions} onChange={handleChange} rows={5} className="form-input font-mono text-xs" /></div>
                        <div><label className="form-label">Software de Servidor</label><textarea name="mainServerSoftware" value={plugin.mainServerSoftware} onChange={handleChange} rows={5} className="form-input font-mono text-xs" /></div>
                        <div><label className="form-label">Dependências</label><textarea name="dependencies" value={plugin.dependencies} onChange={handleChange} rows={5} className="form-input font-mono text-xs" /></div>
                    </div>
                     <p className="text-xs text-gray-500 mt-2">Os campos de compatibilidade devem ser um array JSON válido. Ex: ["1.18.x", "1.19.x"]</p>
                </div>

                {/* Seção de Categorias */}
                <div className="bg-white p-6 rounded-lg shadow-subtle">
                    <h2 className="text-xl font-bold text-text-primary border-b pb-3 mb-4">Categorias</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {availableCategories.map((category) => (
                            <label key={category.id} className="flex items-center bg-gray-50 p-2 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-100">
                                <input type="checkbox" checked={selectedCategoryIds.includes(category.id)} onChange={(e) => handleCategoryChange(category.id, e.target.checked)} className="form-checkbox" />
                                <span className="ml-2 text-sm font-medium text-gray-700">{category.icon} {category.name}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end gap-4 pt-4 border-t mt-6">
                    <button type="submit" disabled={saving} className="w-full md:w-auto px-8 py-3 text-sm font-semibold text-white bg-brand-blue rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
                        {saving ? 'Salvando Alterações...' : 'Salvar Todas as Alterações'}
                    </button>
                </div>
            </form>

            {/* Seção de Versões */}
            <section className="bg-white p-6 rounded-lg shadow-subtle space-y-6">
                <h2 className="text-xl font-bold text-text-primary border-b pb-4">Gerenciar Versões</h2>
                <Disclosure>
                    {({ open }) => (
                        <div className="bg-gray-50 rounded-lg border">
                            <Disclosure.Button className="flex justify-between items-center w-full p-4 font-semibold text-lg text-brand-blue hover:bg-blue-100">
                                <span className="flex items-center gap-2"><PlusIcon /> Adicionar Nova Versão</span>
                                <ChevronUpIcon className={`transform transition-transform ${open ? 'rotate-180' : ''} w-5 h-5`} />
                            </Disclosure.Button>
                            <Transition as={Fragment} enter="transition duration-100 ease-out" enterFrom="opacity-0" enterTo="opacity-100" leave="transition duration-75 ease-out" leaveFrom="opacity-100" leaveTo="opacity-0">
                                <Disclosure.Panel><AddVersionForm pluginId={pluginId!} onVersionAdded={fetchPluginData} /></Disclosure.Panel>
                            </Transition>
                        </div>
                    )}
                </Disclosure>
                <div className="space-y-4 pt-4">
                    <h3 className="font-bold text-text-primary">Versões Existentes ({plugin.versions.length})</h3>
                    {plugin.versions.length === 0 && <p className="text-center text-text-secondary py-4">Nenhuma versão cadastrada.</p>}
                    {plugin.versions.map(v => (
                        <VersionManager key={v.id} version={v} onUpdate={fetchPluginData} onDelete={handleDeleteVersion} />
                    ))}
                </div>
            </section>
        </div>
    );
}